/**
 * Supabase client — lazy-loaded.
 *
 * The 27 KB GoTrueClient + adjacent libs aren't needed for first paint
 * on any page. AuthProvider awaits ensureSupabase() inside a mount
 * effect; the dynamic import() lets webpack code-split @supabase/
 * supabase-js into its own chunk, off the critical path.
 *
 * The mutable `supabase` export is a live binding — once AuthProvider
 * resolves the init promise, every static `import { supabase }` across
 * the app starts seeing the real client. The 9 non-auth consumers
 * (Profile, Admin, scores, etc.) all live behind lazy-loaded routes
 * that don't mount until after AuthProvider has initialized, so they
 * never observe the null state.
 *
 * Anon key is safe in the client bundle — access control is enforced
 * by Postgres Row-Level-Security (see supabase/migrations/001_init.sql).
 */

const URL = process.env.REACT_APP_SUPABASE_URL;
const KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!URL || !KEY) {
  // eslint-disable-next-line no-console
  console.warn(
    '[supabase] REACT_APP_SUPABASE_URL or REACT_APP_SUPABASE_ANON_KEY missing — auth/scoring disabled.'
  );
}

export const isSupabaseEnabled = !!(URL && KEY);

// eslint-disable-next-line import/no-mutable-exports
export let supabase = null;

let _initPromise = null;

/**
 * Lazily load @supabase/supabase-js and create the shared client.
 * Idempotent: subsequent calls return the same client.
 * Returns null if env vars are missing.
 */
export function ensureSupabase() {
  if (supabase) return Promise.resolve(supabase);
  if (!isSupabaseEnabled) return Promise.resolve(null);
  if (_initPromise) return _initPromise;
  _initPromise = import('@supabase/supabase-js').then(({ createClient }) => {
    supabase = createClient(URL, KEY, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
      },
    });
    return supabase;
  });
  return _initPromise;
}
